export type PracticeLocale = "en" | "he";

/** localStorage key for the practice UI language. */
export const PRACTICE_LOCALE_KEY = "mathboard-practice-locale";

/**
 * English practice entry. This path is always English, even when a
 * Hebrew choice is saved.
 */
export const ENGLISH_PRACTICE_PATH = "/practice";

/**
 * Crawlable Hebrew practice entry. Same screen as `/practice`.
 * The path is the ads final URL. The language switch navigates here
 * instead of changing language on the English URL.
 */
export const HEBREW_PRACTICE_PATH = "/he/practice";

/** Funnel query keys kept when switching between the two practice URLs. */
const PRACTICE_SWITCH_QUERY_KEYS = [
  "grade",
  "level",
  "subject",
  "skill",
  "path",
] as const;

export function isPracticeLocale(value: unknown): value is PracticeLocale {
  return value === "en" || value === "he";
}

/**
 * `?locale=he` or `?lang=he` (and the same for `en`).
 * Used by the UI on load and by recording scripts that open a local build.
 */
export function localeFromSearch(search: string): PracticeLocale | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  const value = params.get("locale") ?? params.get("lang");
  return isPracticeLocale(value) ? value : null;
}

/**
 * `/practice` is English and `/he/practice` is Hebrew, with or without a
 * trailing slash. Other paths, including question and blank-sheet URLs,
 * do not name a language.
 */
export function localeFromPath(pathname: string): PracticeLocale | null {
  const raw = pathname.split("?")[0].split("#")[0];
  let path = raw;
  try {
    path = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (path.length > 1) path = path.replace(/\/+$/, "");
  if (path === HEBREW_PRACTICE_PATH) return "he";
  if (path === ENGLISH_PRACTICE_PATH) return "en";
  return null;
}

/**
 * Link to the other practice language, keeping grade and topic query params.
 * `locale` and `lang` are dropped so the path is the only language signal.
 */
export function practiceLocaleSwitchHref(
  targetPath: string,
  fullPath: string,
): string {
  const query = fullPath.includes("?")
    ? fullPath.slice(fullPath.indexOf("?") + 1).split("#")[0]
    : "";
  const params = new URLSearchParams(query);
  const next = new URLSearchParams();
  for (const key of PRACTICE_SWITCH_QUERY_KEYS) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `${targetPath}?${qs}` : targetPath;
}

/** `he`, `he-IL`, and the legacy `iw` tag (any region) count as Hebrew. */
export function isHebrewLanguageTag(tag: string): boolean {
  const primary = tag.trim().toLowerCase().replace(/_/g, "-").split("-")[0];
  return primary === "he" || primary === "iw";
}

/** Hebrew when any browser language tag is Hebrew; otherwise English. */
export function localeFromLanguages(
  languages: readonly string[] | null | undefined,
): PracticeLocale {
  if (!languages) return "en";
  return languages.some(isHebrewLanguageTag) ? "he" : "en";
}

const ISRAEL_TIME_ZONES = new Set(["asia/jerusalem", "asia/tel_aviv"]);

export function isIsraelTimeZone(timeZone: string | null | undefined): boolean {
  return ISRAEL_TIME_ZONES.has((timeZone ?? "").trim().toLowerCase());
}

/**
 * True when the client looks Israeli or Hebrew: browser language
 * (`he`, `he-IL`, `iw`) or an Israel timezone.
 */
export function isHebrewEnvironment(input: {
  languages?: readonly string[] | null;
  timeZone?: string | null;
}): boolean {
  if (input.languages?.some(isHebrewLanguageTag)) return true;
  return isIsraelTimeZone(input.timeZone);
}

/**
 * `/practice` and `/he/practice` always use the path, ahead of a query
 * param, a saved choice, and the Cypress English lock.
 * On every other path, a `?locale=` / `?lang=` query wins, then a saved
 * choice, then English. Browser language does not change the default.
 * Cypress stays English except on those two practice URLs.
 */
export function resolvePracticeLocale(input: {
  search?: string;
  pathname?: string;
  stored?: string | null;
  languages?: readonly string[] | null;
  cypress?: boolean;
}): PracticeLocale {
  const fromPath = localeFromPath(input.pathname ?? "");
  if (fromPath) return fromPath;
  if (input.cypress) return "en";
  const fromQuery = localeFromSearch(input.search ?? "");
  if (fromQuery) return fromQuery;
  if (isPracticeLocale(input.stored)) return input.stored;
  return "en";
}
